import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";

// Module-level guard: survives component re-mounts triggered by setUser/AuthProvider re-render.
// Emergent session_id is single-use — a second exchange call returns 404 "user_data_not_found".
const processedSessions = new Set();

/**
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 * Handles Emergent OAuth redirect with session_id in URL fragment.
 */
export default function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) {
      // No session_id in hash. This can happen if the hash was already cleared by a
      // previous mount of this component (re-render race triggered by setUser).
      // In that case, the session exchange is already in-flight or done — let the
      // ProtectedRoute on /dashboard decide based on real auth state.
      navigate(processedSessions.size > 0 ? "/dashboard" : "/login", { replace: true });
      return;
    }
    const sessionId = decodeURIComponent(match[1]);

    // Module-level dedupe: if a previous mount already used this session_id, don't retry.
    if (processedSessions.has(sessionId)) {
      return;
    }
    processedSessions.add(sessionId);

    (async () => {
      try {
        // Clean URL hash FIRST so a re-mount cannot re-read the session_id.
        window.history.replaceState(null, "", window.location.pathname);
        const { data } = await api.post("/auth/session", { session_id: sessionId });
        // IMPORTANT: navigate FIRST (so useLocation updates and AuthCallback unmounts),
        // THEN setUser. If we setUser first, AppRouter re-renders while useLocation
        // still has the session_id hash → AuthCallback re-mounts → empty hash → /login.
        navigate("/dashboard", { replace: true, state: { user: data.user } });
        setUser(data.user);
      } catch (e) {
        console.error("Auth session exchange failed", e);
        navigate("/login", { replace: true });
      }
    })();
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center" data-testid="auth-callback-loading">
      <div className="text-center">
        <div className="h-12 w-12 mx-auto rounded-full border-2 border-slate-200 border-t-[#002FA7] animate-spin"></div>
        <p className="mt-4 text-slate-500">Signing you in…</p>
      </div>
    </div>
  );
}
